import { DummyToRememberPeopleToCast, EntityDescriptor, FieldDescriptor } from "@crispico/foundation-react";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { entityDescriptors } from "@crispico/foundation-react/entity_crud/entityCrudConstants";
import { AssociationFieldRenderer } from "@crispico/foundation-react/entity_crud/fieldRenderers/AssociationFieldRenderer";
import { contextMenuActions } from "components/ContextMenuRegistry";
import { CreateMissionForProposedTimeSlotAction } from "./CreateMissionForProposedTimeSlotAction";
import { AppMetaTempGlobals } from "@crispico/foundation-react/AppMetaTempGlobals";
import { Item } from '@crispico/react-timeline-10000';
import moment from "moment-timezone";
import _ from "lodash";
import { EquipmentResourceProposedTimeSlot, XopsInitializationsForClient } from "app";
import { GanttUtils } from "pages/gantt/GanttUtils";
import { Mission } from "types";

const ONE_HOUR_IN_MS = 60 * 60 * 1000;
const ONE_MIN_IN_MS = 60 * 1000;

class ResourceFieldDescriptor extends FieldDescriptor {

    renderField(entity: any, _?: DummyToRememberPeopleToCast): React.ReactNode {
        const resource = this.getFieldValue(entity);
        if (!resource) {
            return null;
        }
        const ed = entityDescriptors[resource.__typename];
        return <AssociationFieldRenderer entity={resource} value={resource} asLink asLabel
            fieldDescriptor={new FieldDescriptor()} innerEntityDescriptor={ed} />
    }
}

export class ProposedTimeSlotEntityDescriptor extends EntityDescriptor {

    constructor() {
        super({ name: "ProposedTimeSlot", showInUI: false });
    }

    protected customize() {
        this.addFieldDescriptor({ name: "startTime", type: FieldType.date })
            .addFieldDescriptor({ name: "endTime", type: FieldType.date })
            .addFieldDescriptor({ name: "resource" }, new ResourceFieldDescriptor());
    }

    /**
     * Important: This calculation assumes missions are pre-sorted by startTime and grouped by equipmentResource.
     */
    static getProposedTimeSlots(missions: Mission[]): (Item & { entity: any })[] {

        function calculateEndTime(startTime: number, isLastMission: boolean, isLastMissionForEquipment: boolean, settings: EquipmentResourceProposedTimeSlot): number {
            if (settings.createMissionOfInactivityType && settings.inactivityType?.duration) {
                return startTime + (settings.inactivityType.duration * ONE_MIN_IN_MS);
            }

            if (settings.duration && !isLastMissionForEquipment) {
                return startTime + settings.duration;
            }

            const defaultDuration = (isLastMission || isLastMissionForEquipment)
                ? ONE_HOUR_IN_MS
                : Math.max(0, settings.minRequiredTimeBetweenMissions - 2 * settings.offset);

            return startTime + defaultDuration;
        }

        function createInitialProposedSlots(mission: Mission, nextMission: Mission | undefined, settingsList: EquipmentResourceProposedTimeSlot[], nextMissionStartTime: number | undefined, isDifferentEquipmentType: boolean, isDifferentEquipment: boolean): { slots: (Item & { entity: any })[], slotsWithoutDurationIndexes: number[] } {
            let currentSlotStartTime = moment(mission.endTime).valueOf();
            const slots: (Item & { entity: any })[] = [];
            const slotsWithoutDurationIndexes: number[] = [];

            settingsList.forEach((settings, index) => {
                const timeBetweenMissions = nextMission ? nextMissionStartTime! - currentSlotStartTime : 0;
                const isSameTypeAsProposedSlot = settings.createMissionOfType === mission.type?.id || settings.createMissionOfInactivityType === mission.inactivityType?.id;
                const isTimeSufficient = settings.minRequiredTimeBetweenMissions <= timeBetweenMissions;

                if (!isSameTypeAsProposedSlot && (!nextMission || isDifferentEquipmentType || isTimeSufficient || isDifferentEquipment)) {
                    currentSlotStartTime += settings.offset;
                    const endTime: number = calculateEndTime(currentSlotStartTime, !nextMission, isDifferentEquipment, settings);

                    const entity = {
                        type: settings.missionType,
                        inactivityType: settings.inactivityType,
                        startTime: currentSlotStartTime,
                        endTime,
                        resource: mission.equipmentResource,
                        equipmentType: mission.equipmentType
                    };

                    const slot: Item & { entity: any } = {
                        key: GanttUtils.toEntityUid("ProposedTimeSlot", mission.id) + "_" + index,
                        row: mission.equipmentResource.id,
                        start: currentSlotStartTime,
                        end: endTime,
                        entity
                    };

                    slots.push(slot);

                    if (!settings.duration && !settings.inactivityType?.duration) {
                        slotsWithoutDurationIndexes.push(slots.length - 1);
                    }
                    currentSlotStartTime = endTime + settings.offset;
                }
            });

            return { slots, slotsWithoutDurationIndexes };
        }

        function adjustSlotDurations(slots: (Item & { entity: any })[], slotsWithoutDurationIndexes: number[], nextMission: Mission | undefined, nextMissionStartTime: number | undefined, isDifferentEquipmentType: boolean, isDifferentEquipment: boolean): (Item & { entity: any })[] {
            const lastSlot = slots[slots.length - 1];
            const lastSlotEndTime = lastSlot ? Number(lastSlot.end) : 0;
            const timeBetweenMissions = (nextMission && !isDifferentEquipmentType && !isDifferentEquipment) ? nextMissionStartTime! - lastSlotEndTime : 0;
            const chunk = (timeBetweenMissions > 0 && slotsWithoutDurationIndexes.length > 0) ? timeBetweenMissions / slotsWithoutDurationIndexes.length : 0;
            let accumulatedShift = 0;

            return slots.map((slot, index) => {
                const isWithoutDuration = slotsWithoutDurationIndexes.includes(index);
                const shift = isWithoutDuration ? chunk : 0;

                const newSlot: Item & { entity: any } = {
                    ...slot,
                    start: Number(slot.start) + accumulatedShift,
                    end: Number(slot.end) + accumulatedShift + shift
                };

                newSlot.entity.startTime = newSlot.start;
                newSlot.entity.endTime = newSlot.end;

                accumulatedShift += shift;

                return newSlot;
            });
        }

        function generateProposedSlots(mission: Mission, nextMission: Mission | undefined, settingsList: EquipmentResourceProposedTimeSlot[], nextMissionStartTime: number | undefined, isDifferentEquipmentType: boolean, isDifferentEquipment: boolean): (Item & { entity: any })[] {
            const { slots, slotsWithoutDurationIndexes } = createInitialProposedSlots(mission, nextMission, settingsList, nextMissionStartTime, isDifferentEquipmentType, isDifferentEquipment);
            return adjustSlotDurations(slots, slotsWithoutDurationIndexes, nextMission, nextMissionStartTime, isDifferentEquipmentType, isDifferentEquipment);
        }

        const { proposedTimeSlots } = (AppMetaTempGlobals.appMetaInstance.helperAppContainer.dispatchers.getState().appContainerContextValue.initializationsForClient as XopsInitializationsForClient).equipmentResourceSettings;
        const proposedTimeSlotsMap = _.groupBy(_.sortBy(proposedTimeSlots, 'importance').reverse(), "equipmentTypeId");
        const result: (Item & { entity: any })[] = [];

        for (let i = 0; i < missions.length; i++) {
            const mission = missions[i];
            const nextMission = missions[i + 1];
            const proposedTimeSlotsPerEquipmentType = proposedTimeSlotsMap[mission.equipmentType?.id];

            if (!proposedTimeSlotsPerEquipmentType || proposedTimeSlotsPerEquipmentType.length === 0) {
                continue;
            }

            const nextMissionStartTime = nextMission ? moment(nextMission.startTime).valueOf() : undefined;
            const isDifferentEquipmentType = nextMission?.equipmentType?.id !== mission.equipmentType?.id;
            const isDifferentEquipment = nextMission?.equipmentResource?.id !== mission.equipmentResource?.id;
            const slotsAfterMission = generateProposedSlots(
                mission,
                nextMission,
                proposedTimeSlotsPerEquipmentType,
                nextMissionStartTime,
                isDifferentEquipmentType,
                isDifferentEquipment
            );

            result.push(...slotsAfterMission);
        }
        return result;
    }
}

contextMenuActions.push(CreateMissionForProposedTimeSlotAction);
